import React, { createContext, useState, useContext, ReactNode } from "react"

interface MintFlowContextType {
  currentStep: number
  setCurrentStep: (step: number) => void
  delegationPercentage: number
  setDelegationPercentage: (percentage: number) => void
  delegationUnits: number
  setDelegationUnits: (units: number) => void
  isAOWalletConnected: boolean
  setAOWalletConnected: (connected: boolean) => void
  isEthWalletConnected: boolean
  setEthWalletConnected: (connected: boolean) => void
  delegationComplete: boolean
  setDelegationComplete: (complete: boolean) => void
  depositComplete: boolean
  setDepositComplete: (complete: boolean) => void
}

const defaultContext: MintFlowContextType = {
  currentStep: 1,
  setCurrentStep: () => {},
  delegationPercentage: 100,
  setDelegationPercentage: () => {},
  delegationUnits: 0,
  setDelegationUnits: () => {},
  isAOWalletConnected: false,
  setAOWalletConnected: () => {},
  isEthWalletConnected: false,
  setEthWalletConnected: () => {},
  delegationComplete: false,
  setDelegationComplete: () => {},
  depositComplete: false,
  setDepositComplete: () => {},
}

export const MintFlowContext = createContext<MintFlowContextType>(defaultContext)

interface MintFlowProviderProps {
  children: ReactNode
}

export const MintFlowProvider: React.FC<MintFlowProviderProps> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [delegationPercentage, setDelegationPercentage] = useState<number>(100)
  const [delegationUnits, setDelegationUnits] = useState<number>(0)
  const [isAOWalletConnected, setAOWalletConnected] = useState<boolean>(false)
  const [isEthWalletConnected, setEthWalletConnected] = useState<boolean>(false)
  const [delegationComplete, setDelegationComplete] = useState<boolean>(false)
  const [depositComplete, setDepositComplete] = useState<boolean>(false)

  // Automatically advance steps based on state changes
  React.useEffect(() => {
    if (isAOWalletConnected && currentStep === 1) {
      setCurrentStep(2)
    }

    if (delegationComplete && currentStep === 2) {
      setCurrentStep(3)
    }

    if (depositComplete && currentStep === 3) {
      setCurrentStep(4)
    }
  }, [isAOWalletConnected, delegationComplete, depositComplete, currentStep])

  const value = {
    currentStep,
    setCurrentStep,
    delegationPercentage,
    setDelegationPercentage,
    delegationUnits,
    setDelegationUnits,
    isAOWalletConnected,
    setAOWalletConnected,
    isEthWalletConnected,
    setEthWalletConnected,
    delegationComplete,
    setDelegationComplete,
    depositComplete,
    setDepositComplete,
  }

  return <MintFlowContext.Provider value={value}>{children}</MintFlowContext.Provider>
}

// Custom hook to use the mint flow context
export const useMintFlow = (): MintFlowContextType => useContext(MintFlowContext)

export default MintFlowProvider
